<template>
  <b-card v-if="selectedUser">
    <edit-profile-form v-bind="selectedUser" @input="updateUser" in-modal/>

    <p class="text-dark">Changing your password will sign you out of all other sessions.</p>
    <form method="POST" @submit.prevent="changePassword">
      <b-form-group>
        <b-form-input id="oldPassword" type="password" placeholder="Current password" :state="formState('oldPassword')" v-model="form.oldPassword"/>
        <div v-if="typeof formValid.oldPassword === 'string'" class="invalid-feedback">
          <template v-if="formValid.oldPassword === 'EMPTY'">Please re-enter your chosen password</template>
          <template v-else>{{ formValid.oldPassword }}</template>
        </div>
      </b-form-group>

      <b-form-group>
        <b-form-input id="password" type="password" placeholder="New password" :state="formState('password')" v-model="form.password"/>
        <b-form-text v-if="passwordPolicy">
          Your password must:
          <span v-if="passwordPolicy.requireUppercase" class="d-block ml-1"
            :class="{ 'text-success': passwordValid.requireUppercase === true, 'text-danger': passwordValid.requireUppercase === false }">
            • Contain an uppercase letter (A-Z)
          </span>
          <span v-if="passwordPolicy.requireLowercase" class="d-block ml-1"
            :class="{ 'text-success': passwordValid.requireLowercase === true, 'text-danger': passwordValid.requireLowercase === false }">
            • Contain a lowercase letter (a-z)
          </span>
          <span v-if="passwordPolicy.requireNumbers" class="d-block ml-1"
            :class="{ 'text-success': passwordValid.requireNumbers === true, 'text-danger': passwordValid.requireNumbers === false }">
            • Contain a number (0-9)
          </span>
          <span v-if="passwordPolicy.requireSymbols" class="d-block ml-1"
            :class="{ 'text-success': passwordValid.requireSymbols === true, 'text-danger': passwordValid.requireSymbols === false }">
            • Contain a symbol (^ $ * . [ ] { } ( ) ? " ! @ # % &amp; / \ , &gt; &lt; ' : ; | _ ~ `)
          </span>
          <span v-if="passwordPolicy.minimumLength" class="d-block ml-1"
            :class="{ 'text-success': passwordValid.minimumLength === true, 'text-danger': passwordValid.minimumLength === false }">
            • Be longer than {{ passwordPolicy.minimumLength }} characters
          </span>
        </b-form-text>
      </b-form-group>

      <b-form-group>
        <b-form-input id="passwordConfirmation" type="password" placeholder="Confirm new password" :state="formState('passwordConfirm')" v-model="form.passwordConfirm"/>
        <div v-if="typeof formValid.passwordConfirm === 'string'" class="invalid-feedback">
          <template v-if="formValid.passwordConfirm === 'EMPTY'">Please re-enter your chosen password</template>
          <template v-else-if="formValid.passwordConfirm === 'CONFIRM_MISMATCH'">Passwords should match</template>
          <template v-else>{{ formValid.passwordConfirm }}</template>
        </div>
      </b-form-group>

      <b-form-group>
        <b-btn class="w-100 text-left" :disabled="!isFormValid || working" size="md" type="submit" variant="primary">
          <template v-if="working">Resetting password...</template>
          <template v-else>Reset password</template>
        </b-btn>
      </b-form-group>
    </form>
  </b-card>
</template>

<script>
import formValidationMixin from '@src/mixins/formValidation';
// import ContactsUploadContainer from '@/components/contacts-upload/Container';
import EditProfileForm from '@/components/users/EditProfileForm';
import AuthChangePasswordMutation from '@/graphql/mutations/AuthChangePassword.gql';
import UserEditQuery from '@/graphql/queries/UserEdit.gql';
import UserEditMutation from '@/graphql/mutations/UserEdit.gql';
import { matchGraphQLError, toDataURI } from '@src/lib/utils';

export default {
  components: {
    EditProfileForm,
    // ContactsUploadContainer,
  },
  mixins: [ formValidationMixin ],
  data: () => ({
    cognito: null,
    form: {},
    working: false,
  }),
  metaInfo: {
    title: 'Your Profile',
  },
  apollo: {
    selectedUser: {
      query: UserEditQuery,
      variables() {
        const { userID } = this;
        return { userID };
      },
      update: ({ company: { cognito, value, ...company } }) => ({ company, value }),
      result({ loading, data }) {
        if (!loading && data && data.company) {
          const { cognito } = data.company;
          this.cognito = cognito;
        }
      },
      error(err) {
        this.$notifyFatalErr(err);
      },
      skip() {
        return !this.userID;
      },
      fetchPolicy: 'network-only',
    },
  },
  formValidation: {
    oldPassword(value) {
      if (!value) {
        return 'EMPTY';
      } else {
        return true;
      }
    },
    password(value) {
      if (typeof this.forcePasswordInvalid === 'boolean') {
        return this.forcePasswordInvalid;
      } else if (typeof value === 'string') {
        return this.passwordPolicy && this.passwordValid
          ? Object.keys(this.passwordValid).reduce((r, key) => r && (this.passwordValid[key] !== false), true)
          : true;
      } else {
        return null;
      }
    },
    passwordConfirm(value, { password }) {
      if (password && value !== password) {
        return 'CONFIRM_MISMATCH';
      } else if (!value) {
        return 'EMPTY';
      } else {
        return true;
      }
    },
  },
  computed: {
    userID() {
      if (this.$root.user && this.$root.user.id) {
        return this.$root.user.id;
      } else {
        return null;
      }
    },
    isFormValid() {
      const invalidInputs = Object.keys(this.formValid).filter(key => this.formValid[key] !== true);
      if (invalidInputs.length) return false;
      return true;
    },
    passwordPolicy() {
      const { passwordPolicy = {} } = this.cognito || {};
      return passwordPolicy;
    },
    passwordValid() {
      const { passwordPolicy } = this.cognito || {};
      const { requireUppercase, requireLowercase, requireNumbers, requireSymbols, minimumLength } = passwordPolicy || {};
      const { password } = this.form || {};
      const hasPassword = typeof password === 'string';
      return {
        requireUppercase: requireUppercase && hasPassword ? /[A-Z]/g.test(password) : null,
        requireLowercase: requireLowercase && hasPassword ? /[a-z]/g.test(password) : null,
        requireNumbers: requireNumbers && hasPassword ? /[0-9]/g.test(password) : null,
        // eslint-disable-next-line no-useless-escape
        requireSymbols: requireSymbols && hasPassword ? /[\^$*.\[\]{}()?"!@#%&/,><':;|_~`]/g.test(password) : null,
        minimumLength: minimumLength && hasPassword ? password.length >= minimumLength : null,
      };
    },
  },
  methods: {
    formState(key) {
      return this.formValid[key] === true ? true : (typeof this.formValid[key] === 'string' ? false : null);
    },
    async updateUser({ avatar, ...update }) {
      const { userID } = this;
      this.formErr = null;

      try {
        await this.$apollo.mutate({
          mutation: UserEditMutation,
          variables: { userID, update },
        });

        this.$notification({
          title: 'Profile saved',
          message: 'Your account has been updated',
          variant: 'success',
        });
      } catch (err) {
        this.$notifyErr(err);
        this.$log.error(err);
        throw err;
      }
    },
    async changePassword() {
      try {
        const { oldPassword, password, passwordConfirm } = this.form;
        this.$assert(password && password === passwordConfirm, new Error('Passwords do not match'), {
          code: 'PASSWORDS_MISMATCH',
        });

        this.working = true;

        await this.$apollo.mutate({
          mutation: AuthChangePasswordMutation,
          variables: {
            change: {
              password: toDataURI('text/plain;charset=utf8', oldPassword),
              newPassword: toDataURI('text/plain;charset=utf8', password),
            },
          },
        });

        this.$notification({
          title: 'Password changed',
          message: 'Your password has been successfully changed!',
          variant: 'success',
        });

        this.$refs.changePassword.hide();
        this.form = {};
        this.working = false;
      } catch (err) {
        if (matchGraphQLError(err, 'extensions.message', 'Incorrect username or password.')) {
          this.$notifyErr('Incorrect current password, please try again.');
        } else {
          this.$notifyErr(err, { prepend: 'Failed to change password:' });
        }

        this.$log.error(err);
        this.working = false;
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/profile/AccountEditor.vue